import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import UserInput from '@/components/UserInput';
import { Button, Modal, App, message as antdMessage } from 'antd';
import { InterviewStatus, MessageDTO, UserMessageDTO } from '@/types';
import {
  getInterview,
  getMessages,
  startInterview,
  completeInterview,
  createMessage,
} from '@/services/interview';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  interviewMessagesState,
  allInterviewMessagesState,
  currentInterviewNumberState,
  interviewDisabledState,
  nextMessageIdState,
} from '@/store/message';
import { TTSStreamResult, useTTS } from '@/hooks/useTTS';

const Interview = () => {
  const { modal } = App.useApp();
  const { interviewNumber } = useParams();
  const navigate = useNavigate();

  // Recoil 状态管理
  const messages = useRecoilValue(interviewMessagesState);
  const [, setAllMessages] = useRecoilState(allInterviewMessagesState);
  const [disabled, setDisabled] = useRecoilState(interviewDisabledState);
  const setCurrentInterviewNumber = useSetRecoilState(currentInterviewNumberState);

  const nextMessageId = useRecoilValue(nextMessageIdState);
  const [ttsCurrentId, setTtsCurrentId] = useState<string | null>(null);
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<number>(0);
  const [hasMore, setHasMore] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // TTS 管理
  const {
    playing: ttsPlaying,
    loading: ttsLoading,
    connect: ttsConnect,
    skip: ttsSkip,
    destory: ttsDestory,
  } = useTTS({
    onStream: (ttsResult: TTSStreamResult) => {
      setTtsCurrentId(ttsResult.messageId);
      setAllMessages((prev) => {
        const currentMessages = prev[ttsResult.interviewNumber] || [];
        return {
          ...prev,
          [ttsResult.interviewNumber]: currentMessages.map((item) =>
            item.messageId === ttsResult.messageId
              ? {
                  ...item,
                  content: ttsResult.content,
                  fullContent: ttsResult.isFinal ? ttsResult.content : undefined,
                  thinking: false,
                  timestamp: ttsResult.timestamp,
                }
              : item,
          ),
        };
      });
    },
    onError: () => {
      // TTS 错误处理
    },
  });

  // 加载消息历史（初始化加载最近50条）
  useEffect(() => {
    if (!interviewNumber) return;

    let checkHandle: number | undefined = undefined;
    let audioContext: AudioContext | null = null;

    const init = async () => {
      try {
        setLoading(true);
        const interviewNum = Number(interviewNumber);
        const interview = await getInterview(interviewNum);

        const response = await getMessages(interviewNum, {
          cursor: 0,
          limit: 50,
        });

        let ttsMessageId = nextMessageId;
        const messages = response.items.map((item) => ({ ...item, fullContent: item.content }));
        if (messages.length > 0) {
          const messagesWithoutSystem = messages.filter((item) => item.role !== 'system');
          if (messagesWithoutSystem.length > 0) {
            const lastMessage = messagesWithoutSystem[messagesWithoutSystem.length - 1];
            if (lastMessage.role === 'user') {
              ttsMessageId = lastMessage.nextMessageId;
            }
          }
        }
        if (ttsMessageId) {
          ttsConnect(interviewNum, ttsMessageId);
        }

        // 初始化时，直接设置消息列表（最新的数据在最后）
        setAllMessages((prev) => ({
          ...prev,
          [interviewNum]: [...(prev[interviewNum] || []), ...messages],
        }));
        setNextCursor(response.nextCursor);
        setHasMore(response.hasMore);

        // 设置当前面试编号（用于过滤消息）
        setCurrentInterviewNumber(interviewNum);

        return interview.status;
      } catch {
        // 加载消息失败
      } finally {
        setLoading(false);
      }
    };

    init()
      .then(async (status: InterviewStatus | undefined) => {
        if (status === 'in_progress' || status === 'pending') {
          const interviewNum = Number(interviewNumber);
          await startInterview(interviewNum);

          setDisabled(false);
          const AudioContextClass =
            window.AudioContext ||
            (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
          audioContext = new AudioContextClass();

          checkHandle = setTimeout(() => {
            modal.info({
              title: '提示',
              content: '面试需要使用声音功能，请确保不会影响到他人',
              okText: '知道了',
              onOk: () => {
                if (messagesEndRef.current) {
                  messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
                }
              },
            });
          }, 2000);

          audioContext.resume().then(() => {
            // 检查组件是否仍然挂载
            if (checkHandle) {
              clearTimeout(checkHandle);
            }
            if (messagesEndRef.current) {
              messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          });
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
      });

    // 清理函数
    return () => {
      if (checkHandle) {
        clearTimeout(checkHandle);
      }
      // 清理 AudioContext（如果已创建）
      if (audioContext && audioContext.state !== 'closed') {
        audioContext.close().catch(() => {
          // 忽略关闭错误
        });
      }
      // 清理当前面试编号
      setCurrentInterviewNumber(null);
      // 清理当前面试的消息，避免消息积压
      if (interviewNumber) {
        const interviewNum = Number(interviewNumber);
        setAllMessages((prev: Record<number, MessageDTO[]>) => {
          const updated = { ...prev };
          delete updated[interviewNum];
          return updated;
        });
      }
      ttsDestory();
    };
  }, [
    interviewNumber,
    nextMessageId,
    setAllMessages,
    setCurrentInterviewNumber,
    setDisabled,
    modal,
    ttsDestory,
  ]);

  // 处理发送消息
  const handleSendMessage = async (text: string) => {
    if (!interviewNumber) {
      return false;
    }
    if (!text.trim()) {
      antdMessage.warning('不允许发送空消息');
      return false;
    }
    if (ttsPlaying) {
      ttsSkip();
    }
    const interviewNum = Number(interviewNumber);
    try {
      // 添加用户消息占位符
      const userMessage: UserMessageDTO = {
        messageId: `user_holder`,
        nextMessageId: `user_holder`,
        role: 'user',
        content: text,
        timestamp: Date.now(),
      };
      setAllMessages((prev) => {
        const currentMessages = prev[interviewNum] || [];
        return {
          ...prev,
          [interviewNum]: [...currentMessages, userMessage],
        };
      });
      // 通过全局状态发送消息（会自动传入当前面试号）
      const resp = await createMessage(interviewNum, text);

      if (resp.messageId) {
        setAllMessages((prev) => {
          const currentMessages = prev[interviewNum] || [];
          return {
            ...prev,
            [interviewNum]: [
              ...currentMessages.map((item) =>
                item.messageId !== `user_holder`
                  ? item
                  : {
                      ...item,
                      messageId: resp.messageId,
                      timestamp: resp.timestamp,
                      nextMessageId: resp.nextMessageId,
                    },
              ),
              {
                role: 'assistant',
                messageId: resp.nextMessageId,
                timestamp: Number.MAX_SAFE_INTEGER,
                content: '',
                thinking: true,
              },
            ],
          };
        });
      }
      ttsConnect(interviewNum, resp.nextMessageId);
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setAllMessages((prev) => {
        const currentMessages = prev[interviewNum] || [];
        return {
          ...prev,
          [interviewNum]: currentMessages.filter((item) => item.messageId !== `user_holder`),
        };
      });
      return false;
    }
  };

  // 处理结束面试
  const handleEndInterview = () => {
    setCloseModalOpen(true);
  };

  // 加载更多消息（加载更早的数据）
  const handleLoadMore = async () => {
    if (!interviewNumber || !hasMore || loadingMore || nextCursor === 0) return;

    try {
      setLoadingMore(true);
      const interviewNum = Number(interviewNumber);
      const response = await getMessages(interviewNum, {
        cursor: nextCursor,
        limit: 50,
      });
      // 将新加载的数据添加到现有数据的前面（因为是历史消息）
      setAllMessages((prev) => {
        const currentMessages = prev[interviewNum] || [];
        return {
          ...prev,
          [interviewNum]: [
            ...response.items.map((item) => ({ ...item, fullContent: item.content })),
            ...currentMessages,
          ],
        };
      });
      setNextCursor(response.nextCursor);
      setHasMore(response.hasMore);
    } catch {
      // 加载更多消息失败
    } finally {
      setLoadingMore(false);
    }
  };

  // 监听最后一条 assistant 消息完成，自动滚动到底部
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant' && lastMessage.fullContent) {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [messages]);

  return (
    <div className="min-h-[calc(100vh-64px)] lg:min-h-[calc(100vh-80px)] overflow-hidden bg-gradient-to-b from-slate-50 to-indigo-100">
      {/* 提示信息：离开页面将自动结束面试 */}
      {!disabled && (
        <div className="w-full bg-amber-50 border-b border-amber-200 px-6 py-2 text-center">
          <span className="text-sm text-amber-700">
            <span className="font-medium">提示：</span>离开页面将自动结束面试
          </span>
        </div>
      )}
      {/* 主容器 */}
      <div className="max-w-[1440px] h-[calc(100vh-64px-39px)] lg:h-[calc(100vh-80px-39px)] w-full mx-auto py-4 px-6 lg:px-12 flex flex-col gap-4 overflow-hidden">
        {/* 消息区域 */}
        <div className="flex-1 bg-gradient-to-br from-white/95 to-slate-50/95 rounded-2xl shadow-lg overflow-hidden flex flex-col border border-slate-200/80 backdrop-blur-xl z-[1]">
          {/* 滚动区域 */}
          <div
            className={`${
              disabled
                ? 'max-h-[calc(100vh-64px-48px)] lg:max-h-[calc(100vh-80px-48px)]'
                : 'max-h-[calc(100vh-64px-200px-48px)] lg:max-h-[calc(100vh-80px-200px-48px)]'
            } flex-1 overflow-y-auto overflow-x-hidden p-6 flex flex-col gap-5 min-h-0 scrollbar-thin`}
          >
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-slate-500">加载消息中...</span>
              </div>
            ) : (
              <>
                {/* 加载更多按钮 */}
                {hasMore && (
                  <div className="flex justify-center py-2">
                    <Button
                      type="default"
                      loading={loadingMore}
                      onClick={handleLoadMore}
                      className="border-slate-300 text-slate-600 hover:border-indigo-500 hover:text-indigo-600"
                    >
                      {loadingMore ? '加载中...' : '加载更多'}
                    </Button>
                  </div>
                )}
                {messages.map((message: MessageDTO) =>
                  message.role === 'system' ? (
                    <div
                      key={message.messageId}
                      className="w-full flex justify-center animate-slideIn opacity-80"
                    >
                      <div className="bg-gray-200 rounded-xl px-4 py-1 text-[11px] leading-relaxed text-gray-600 font-medium">
                        {message.content}
                      </div>
                    </div>
                  ) : (
                    <div
                      key={message.messageId}
                      className={`flex gap-4 animate-slideIn opacity-80 ${
                        message.role === 'user' ? 'flex-row-reverse' : ''
                      }`}
                    >
                      {/* 头像 */}
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {message.role === 'assistant' ? (
                          <img
                            src="https://project-poster-assets.oss-cn-hangzhou.aliyuncs.com/logo.png"
                            alt="小灵"
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-indigo-500 to-violet-500 text-white text-xl font-bold rounded-full">
                            {window.config?.username?.charAt(0)}
                          </div>
                        )}
                      </div>
                      {/* 消息内容 */}
                      <div
                        className={`max-w-[70%] px-[18px] py-[14px] rounded-[18px] transition-all shadow-sm backdrop-blur-sm border border-slate-200/60 ${
                          message.role === 'assistant'
                            ? 'bg-gradient-to-br from-white/90 to-slate-50/90 border border-slate-200/60 cursor-pointer hover:shadow-lg'
                            : `${
                                message.messageId === 'user_holder' ? 'animate-pulse ' : ''
                              }bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border border-white/20 shadow-indigo-500/20`
                        }`}
                        title={message.messageId}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span
                            className={`text-[13px] font-semibold ${
                              message.role === 'assistant' ? 'text-slate-500' : 'text-white/95'
                            }`}
                          >
                            {message.role === 'assistant' ? '小灵面试官' : window.config?.username}
                          </span>
                          {/* 显示说话动画 */}
                          {ttsCurrentId === message.messageId &&
                            ttsPlaying &&
                            !!message.fullContent && (
                              <>
                                <div className="flex items-center gap-1 group/aiwave">
                                  <span className="w-[3px] h-3 bg-indigo-500 rounded-sm animate-wave"></span>
                                  <span className="w-[3px] h-3 bg-indigo-500 rounded-sm animate-wave [animation-delay:0.1s]"></span>
                                  <span className="w-[3px] h-3 bg-indigo-500 rounded-sm animate-wave [animation-delay:0.2s]"></span>
                                  <Button
                                    type="link"
                                    danger
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      ttsSkip();
                                    }}
                                    className="hidden group-hover/aiwave:inhert"
                                  >
                                    跳过语音播放
                                  </Button>
                                </div>
                              </>
                            )}
                        </div>
                        <div
                          className={`text-[15px] leading-relaxed ${
                            message.role === 'assistant' ? 'text-slate-900' : 'text-white'
                          }`}
                        >
                          {message.role === 'assistant' && message.thinking ? (
                            <span className="animate-pulse text-slate-500">正在思考中...</span>
                          ) : (
                            <span>{message.content}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ),
                )}
                <div ref={messagesEndRef} className="mt-[150px]" />
              </>
            )}
          </div>
        </div>

        {/* 输入区域 */}
        {disabled ? null : (
          <UserInput
            aiSpeaking={ttsLoading}
            onSend={handleSendMessage}
            onEndInterview={handleEndInterview}
            interviewNumber={interviewNumber ? Number(interviewNumber) : undefined}
          />
        )}
      </div>

      {/* 结束面试确认模态框 */}
      <Modal
        open={closeModalOpen}
        onCancel={() => setCloseModalOpen(false)}
        footer={null}
        wrapClassName="text-center"
      >
        <div className="mb-5 flex justify-center">
          <ExclamationCircleOutlined className="text-[64px] text-amber-500" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-3">确认结束面试？</h3>
        <p className="text-[15px] text-slate-500 leading-relaxed mb-8">
          结束后将立即生成面试报告，无法继续回答问题
        </p>
        <div className="flex gap-3">
          <Button type="default" className="flex-1" onClick={() => setCloseModalOpen(false)}>
            继续面试
          </Button>
          <Button
            type="primary"
            className="flex-1"
            onClick={async () => {
              if (!interviewNumber) return;

              try {
                // 调用后端接口结束面试
                const interviewNum = Number(interviewNumber);
                await completeInterview(interviewNum);

                setCloseModalOpen(false);
                setDisabled(true);
                ttsDestory();

                navigate(`/profile`);
              } catch {
                // 结束面试失败，显示错误提示
                antdMessage.error('结束面试失败，请稍后重试');
              }
            }}
          >
            结束面试
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Interview;
